// filepath: apps/web/src/renderers/task/DueDateBadge.tsx
//
// FR-HT-4 / FR-UI-TASK-1 AC-6 — Overdue indicator badge.
//
// Renders a red "Overdue" badge when:
//   - `dueAt` is an ISO-8601 timestamp in the past, AND
//   - `status` is NOT 'completed' or 'cancelled'.
//
// The badge is purely cosmetic — no security semantics.
// The overdue state is derived client-side from `due_at` and `status`
// received from the tasks API. No API change needed.
//
// INV-9: no dangerouslySetInnerHTML — static chrome text only.

import React from "react";

export interface DueDateBadgeProps {
  /** ISO-8601 due-at timestamp, or null if no due date is set. */
  dueAt: string | null;
  /** Current task status. */
  status: "unclaimed" | "claimed" | "completed" | "cancelled";
}

/** Returns true if the given ISO timestamp is in the past. */
function isOverdue(dueAt: string): boolean {
  try {
    return new Date(dueAt).getTime() < Date.now();
  } catch {
    return false;
  }
}

/** Returns a human-readable relative time string for the due date. */
function formatDueDate(dueAt: string): string {
  try {
    const d = new Date(dueAt);
    const now = new Date();
    const diffMs = d.getTime() - now.getTime();
    const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return "Due today";
    if (diffDays === 1) return "Due tomorrow";
    if (diffDays === -1) return "Due yesterday";
    if (diffDays < 0)
      return `Overdue by ${Math.abs(diffDays)} day${Math.abs(diffDays) !== 1 ? "s" : ""}`;
    return `Due in ${diffDays} days`;
  } catch {
    return "";
  }
}

/**
 * DueDateBadge — red badge for overdue tasks, neutral for future/none.
 *
 * AC-6: overdue badge shown when due_at < now && status != completed.
 * The badge is NOT shown for cancelled or completed tasks.
 * The badge is NOT shown when there is no due date.
 */
export function DueDateBadge({
  dueAt,
  status,
}: DueDateBadgeProps): React.ReactElement | null {
  if (!dueAt) return null;
  if (status === "completed" || status === "cancelled") return null;

  if (!isOverdue(dueAt)) return null;

  return (
    <span
      data-testid="due-date-badge"
      title={formatDueDate(dueAt)}
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: "1px 6px",
        borderRadius: "var(--radius-md)",
        backgroundColor: "#dc2626",
        color: "#ffffff",
        fontSize: "11px",
        fontWeight: 600,
        letterSpacing: "0.02em",
        textTransform: "uppercase",
      }}
    >
      Overdue
    </span>
  );
}
